import { kernelQuery } from "../client.js";
import { runContradictionCheck, type ContradictionWarning } from "./smart-capture.js";

export interface SemanticConflictResult {
  chainEntryId: string;
  chainEntryName: string;
  chainEntryDescription: string;
  type: "contradiction" | "supersession" | "duplicate" | "overlap";
  confidence: number;
  explanation: string;
}

export interface ConflictPreflightResult {
  source: "semantic" | "heuristic";
  conflicts: SemanticConflictResult[];
  blockingContradictions: SemanticConflictResult[];
  advisoryWarnings: ContradictionWarning[];
}

const BLOCKING_CONTRADICTION_THRESHOLD = 0.75;

function isBlockingContradiction(conflict: SemanticConflictResult): boolean {
  return conflict.type === "contradiction" && conflict.confidence >= BLOCKING_CONTRADICTION_THRESHOLD;
}

/**
 * Run the staging-grade semantic conflict detector when available.
 * Falls back to the existing heuristic warning path if the bridge is unavailable.
 */
export async function runConflictPreflight(
  name: string,
  description: string,
  collectionHint?: string,
): Promise<ConflictPreflightResult> {
  try {
    const conflicts = await kernelQuery<SemanticConflictResult[]>("chain.detectSemanticConflicts", {
      name,
      description,
      ...(collectionHint ? { collectionHint } : {}),
    });

    return {
      source: "semantic",
      conflicts,
      blockingContradictions: conflicts.filter(isBlockingContradiction),
      advisoryWarnings: [],
    };
  } catch (err) {
    console.error('[conflict-preflight] Semantic conflict detection unavailable, falling back to heuristic:', err instanceof Error ? err.message : String(err));
    const advisoryWarnings = await runContradictionCheck(name, description);
    return {
      source: "heuristic",
      conflicts: [],
      blockingContradictions: [],
      advisoryWarnings,
    };
  }
}
